/**
 * B&W conversion styles (owner 2026-07-31, round 3: inline selector + strength
 * slider). Three variants:
 *   v1 Classic  — Rec.709 luma, the straight conversion. Fixed: no strength.
 *   v2 Dramatic — red filter + contrast. v3 Soft — white-anchored matte lift.
 *
 * v2/v3 carry a STRENGTH (0..100, steps of 10, default 100) that linearly
 * blends the affine conversion between the straight v1 recipe (0) and the
 * style's full recipe (100). Blending affine maps is affine, so every
 * (style, strength) pair is still one exact feColorMatrix — the preview
 * filters and agent/bake.py's _BW_WEIGHTS lerp stay pixel-identical, which is
 * the whole contract. Steps of 10 keep the App-root filter defs enumerable
 * (2 styles x 11 steps) instead of per-instance SVG, and are indistinguishable
 * from a smooth slider by eye. sRGB interpolation is pinned on the defs: the
 * bake mixes in gamma space and the SVG default (linearRGB) previews darker.
 *
 * White stays white at every strength: v1 maps white to 1.0, both full
 * recipes map white to >= 1.0 (v3 exactly 1.0 by construction — offset =
 * 1 - gain), and a lerp of two values >= 1.0 stays >= 1.0, which clamps to
 * white. White borders draw BEFORE the conversion, so this is load-bearing.
 */
import type { BwStyle } from './types';

export const BW_STYLES: readonly BwStyle[] = ['v1', 'v2', 'v3'];

export const BW_STRENGTH_STEP = 10;
export const BW_STRENGTH_DEFAULT = 100;

export interface BwMix {
  weights: readonly [number, number, number];
  gain: number;
  offset: number;
}

/** Full-strength recipes. THE SAME numbers live in agent/bake.py. */
export const BW_MIXES: Record<BwStyle, BwMix> = {
  v1: { weights: [0.2126, 0.7152, 0.0722], gain: 1, offset: 0 },
  v2: { weights: [0.9, 0.1, 0], gain: 1.25, offset: -0.125 },
  v3: { weights: [0.35, 0.6, 0.05], gain: 0.85, offset: 0.15 },
};

export function normalizeBwStyle(v: unknown): BwStyle | null {
  return v === 'v1' || v === 'v2' || v === 'v3' ? v : null;
}

/** Snap to the legal 0..100 step-of-10 grid; anything unusable → default. */
export function clampBwStrength(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : BW_STRENGTH_DEFAULT;
  const snapped = Math.round(n / BW_STRENGTH_STEP) * BW_STRENGTH_STEP;
  return Math.min(100, Math.max(0, snapped));
}

/** Effective affine params at a strength: lerp(v1, style, t). v1 ignores t. */
export function bwEffective(
  style: BwStyle,
  strength: number,
): { row: [number, number, number]; offset: number } {
  const t = style === 'v1' ? 0 : clampBwStrength(strength) / 100;
  const a = BW_MIXES.v1;
  const b = BW_MIXES[style];
  const lerp = (x: number, y: number) => x + (y - x) * t;
  const round = (x: number) => Math.round(x * 1e4) / 1e4;
  return {
    row: [
      round(lerp(a.weights[0] * a.gain, b.weights[0] * b.gain)),
      round(lerp(a.weights[1] * a.gain, b.weights[1] * b.gain)),
      round(lerp(a.weights[2] * a.gain, b.weights[2] * b.gain)),
    ],
    offset: round(lerp(a.offset, b.offset)),
  };
}

/** CSS filter for a B&W print. v1 (and legacy null style) keeps plain
 *  grayscale(1); v2/v3 reference the App-root per-strength filter defs. */
export function bwCssFilter(
  bw: boolean,
  style?: BwStyle | null,
  strength?: number | null,
): string | undefined {
  if (!bw) return undefined;
  if (style === 'v2' || style === 'v3') {
    return `url(#bw-${style}-${clampBwStrength(strength ?? BW_STRENGTH_DEFAULT)})`;
  }
  return 'grayscale(1)';
}

/** feColorMatrix `values`: every RGB output row is the same affine mix. */
export function bwMatrixValues(style: BwStyle, strength: number): string {
  const {
    row: [r, g, b],
    offset,
  } = bwEffective(style, strength);
  const row = `${r} ${g} ${b} 0 ${offset}`;
  return `${row} ${row} ${row} 0 0 0 1 0`;
}

/** Every (style, strength) filter id the App defs must provide. */
export function bwFilterDefList(): { id: string; style: BwStyle; strength: number }[] {
  const out: { id: string; style: BwStyle; strength: number }[] = [];
  for (const style of ['v2', 'v3'] as const) {
    for (let s = 0; s <= 100; s += BW_STRENGTH_STEP) {
      out.push({ id: `bw-${style}-${s}`, style, strength: s });
    }
  }
  return out;
}
